// packages/shared/src/analytics/analytics.ts
// SIL-1529: GA4 + HubSpot loader, Consent Mode v2 defaults, HubSpot consent
// bridge, typed track(), and ui_click delegation. Plain DOM - the exchange
// catalogue pages have no React. Spec:
// docs/superpowers/specs/2026-10-02-ga4-hubspot-tracking-design.md
import type { AnalyticsEventName, AnalyticsEvents, AnalyticsUserProperties, Surface } from './events';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _hsq?: unknown[];
    _hsp?: unknown[];
  }
}

export interface AnalyticsConfig {
  gaMeasurementId: string;
  hubspotPortalId: string;
}

/** EEA + UK + CH: analytics denied until the HubSpot banner says yes. */
export const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO',
  'GB', 'CH',
];

// We run no ads, so the ad signals are denied everywhere.
const ADS_DENIED = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
const SURFACES: readonly Surface[] = ['header', 'footer', 'body', 'modal'];

let gaId: string | null = null;

function gtag(..._args: unknown[]): void {
  // gtag.js reads the Arguments object, not an array.
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer ??= []).push(arguments);
}

function loadScript(src: string, id?: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  if (id) script.id = id;
  document.head.appendChild(script);
}

export function onHubSpotConsent(consent: { categories?: { analytics?: boolean } }): void {
  gtag('consent', 'update', { analytics_storage: consent.categories?.analytics ? 'granted' : 'denied' });
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-cookie-settings]')) {
    event.preventDefault();
    (window._hsp ??= []).push(['showBanner']);
  }

  const el = target.closest<HTMLElement>('[data-track]');
  const elementId = el?.dataset.track;
  if (!el || !elementId) return;
  const rawSurface = el.closest<HTMLElement>('[data-track-surface]')?.dataset.trackSurface;
  const surface = SURFACES.includes(rawSurface as Surface) ? (rawSurface as Surface) : 'body';
  const params: AnalyticsEvents['ui_click'] = { element_id: elementId, surface };
  // Exchange pages are full page loads, so time since navigation start = time since view.
  if (el.dataset.trackTiming !== undefined) params.time_since_view_ms = Math.round(performance.now());
  track('ui_click', params);
}

export function initAnalytics(config: AnalyticsConfig): void {
  if (gaId !== null) return;
  gaId = config.gaMeasurementId;
  window.gtag = gtag;

  gtag('consent', 'default', {
    ...ADS_DENIED,
    analytics_storage: 'denied',
    region: CONSENT_REGIONS,
    wait_for_update: 500,
  });
  // wait_for_update on the global default too: a returning non-EEA visitor
  // who opted out via HubSpot's banner gets the 'denied' update before
  // gtag.js sends its first hit, instead of one granted page_view.
  gtag('consent', 'default', { ...ADS_DENIED, analytics_storage: 'granted', wait_for_update: 500 });
  gtag('js', new Date());
  const debug = new URLSearchParams(window.location.search).get('analytics_debug') === '1';
  gtag('config', gaId, debug ? { debug_mode: true } : {});
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`);

  window._hsq ??= [];
  (window._hsp ??= []).push(['addPrivacyConsentListener', onHubSpotConsent]);
  loadScript(`https://js.hs-scripts.com/${encodeURIComponent(config.hubspotPortalId)}.js`, 'hs-script-loader');

  document.addEventListener('click', onDocumentClick, true);
}

export function track<K extends AnalyticsEventName>(name: K, params: AnalyticsEvents[K]): void {
  if (gaId === null) return;
  gtag('event', name, params);
}

export function setUserProperties(props: AnalyticsUserProperties): void {
  if (gaId === null) return;
  gtag('set', 'user_properties', props);
}

/** `userId` must already be opaque - pass the result of hashUserId(). */
export function setUser(userId: string, props: AnalyticsUserProperties): void {
  if (gaId === null) return;
  gtag('config', gaId, { user_id: userId, send_page_view: false });
  setUserProperties(props);
}

/** HubSpot sends identify only with the next tracking call, hence trackPageView. */
export function identify(email: string): void {
  if (gaId === null) return;
  const hsq = (window._hsq ??= []);
  hsq.push(['identify', { email }]);
  hsq.push(['trackPageView']);
}

export async function hashUserId(sub: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sub));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function __resetAnalyticsForTests(): void {
  document.removeEventListener('click', onDocumentClick, true);
  gaId = null;
  delete window.dataLayer;
  delete window.gtag;
  delete window._hsq;
  delete window._hsp;
}
